import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { X, Send, ShieldCheck, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dogName: '',
    dogSize: '',
    serviceType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        dog_name: formData.dogName,
        dog_size: formData.dogSize,
        service_type: formData.serviceType,
        message: formData.message
      });
      
      toast.success('Thank you! We\'ll contact you within 24 hours.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        dogName: '',
        dogSize: '',
        serviceType: '',
        message: ''
      });
      onClose();
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-stone-100 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-stone-800">Get Your Free Quote</h3>
              <p className="text-sm text-stone-500">We'll respond within 24 hours</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-stone-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-stone-500" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Trust Badge */}
          <div className="flex items-center gap-2 bg-green-50 text-green-700 rounded-lg px-4 py-2 mb-4">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-sm font-medium">Your information is secure & never shared</span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-stone-700 font-medium">Your Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="border-stone-200 focus:border-amber-500 focus:ring-amber-500"
                placeholder="John Smith"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dogName" className="text-stone-700 font-medium">Dog's Name *</Label>
              <Input
                id="dogName"
                name="dogName"
                value={formData.dogName}
                onChange={handleChange}
                required
                className="border-stone-200 focus:border-amber-500 focus:ring-amber-500"
                placeholder="Max"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-stone-700 font-medium">Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="border-stone-200 focus:border-amber-500 focus:ring-amber-500"
              placeholder="john@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-stone-700 font-medium">Phone Number *</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
              className="border-stone-200 focus:border-amber-500 focus:ring-amber-500"
              placeholder="(410) 555-1234"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-stone-700 font-medium">Dog Size *</Label>
              <Select onValueChange={(value) => handleSelectChange('dogSize', value)}>
                <SelectTrigger className="border-stone-200 focus:border-amber-500 focus:ring-amber-500">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small (under 25 lbs)</SelectItem>
                  <SelectItem value="medium">Medium (25-50 lbs)</SelectItem>
                  <SelectItem value="large">Large (50-100 lbs)</SelectItem>
                  <SelectItem value="xlarge">X-Large (100+ lbs)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-stone-700 font-medium">Service Type *</Label>
              <Select onValueChange={(value) => handleSelectChange('serviceType', value)}>
                <SelectTrigger className="border-stone-200 focus:border-amber-500 focus:ring-amber-500">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="overnight">Overnight Boarding</SelectItem>
                  <SelectItem value="daycare">Doggy Daycare</SelectItem>
                  <SelectItem value="extended">Extended Stay</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-stone-700 font-medium">Additional Details</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="border-stone-200 focus:border-amber-500 focus:ring-amber-500 min-h-[80px]"
              placeholder="Tell us about your dog, any special needs, or preferred dates..."
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-6 text-lg rounded-xl transition-all disabled:opacity-70"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Send className="w-5 h-5" />
                Get My Free Quote
              </span>
            )}
          </Button>

          {/* Quick Contact */}
          <div className="pt-4 border-t border-stone-100">
            <p className="text-sm text-stone-500 text-center mb-3">Prefer to email? Reach us directly:</p>
            <div className="flex justify-center">
              <a href="mailto:team@dogboardingbaltimore.com" className="flex items-center gap-2 text-stone-600 hover:text-amber-600 transition-colors">
                <Mail className="w-4 h-4" />
                <span className="text-sm font-medium">team@dogboardingbaltimore.com</span>
              </a>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeadForm;
